import React, {useState, useEffect, useContext, useCallback, useRef} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import OTPInputField from '../../../common/components/Inputs/OTPInputField';
import { confirmEmail } from '../../../../services/Users';
import { parseClaimsFromHeader } from '../../../../utils/auth/ClaimsParser';
import WithActions from "../../../common/components/Wrappers/WithActions";
import ButtonPrimary from "../../../common/components/Buttons/ButtonPrimary";
import DisabledFormState from "../../../common/components/Wrappers/DisabledFormState";
import {ThemeContext} from "styled-components";
import InputValidatorField from '../../../common/components/Inputs/InputValidatorField';
import { IsValidEmail } from '../../../../utils/Validators';
import InputEmail from "../../../common/components/Inputs/InputEmail";
import Typography from "@mui/material/Typography";

const VerifyEmailForm = ({
    formData,
    onChangeAuthForm,
    handleLogin,
    onStepTransition,
    onLoginSuccess,
    onLoginError,
    isLoading,
    setIsLoading,
    csrfToken,
    disabled = false,
    isVisible = true,
    isActive = true,
    onConfirmSubmit,
    isConfirmFlow = false
}) => {
    const [localError, setLocalError] = useState(null);
    const theme = useContext(ThemeContext);
    // Track the last code that was auto-submitted to prevent infinite retries
    const lastAutoSubmittedCode = useRef(null);
    // Track if we're currently in an auto-submit attempt to prevent race conditions
    const isAutoSubmitting = useRef(false);
    // Store handleSubmit in a ref so we can access the latest version without causing effect re-runs
    const handleSubmitRef = useRef(null);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (isLoading) {
            return;
        }

        // Validate form and show errors if invalid
        if (formData.verificationCode.length !== 6) {
            setLocalError("Please enter a complete 6-digit verification code");
            return;
        }

        // Mark this code as attempted immediately to prevent retries
        // This prevents infinite loops if the submission fails
        const currentCode = formData.verificationCode;
        lastAutoSubmittedCode.current = currentCode;

        setIsLoading(true);
        setLocalError(null);

        try {
            // For confirm flow, use the confirm submit handler
            if (isConfirmFlow && onConfirmSubmit) {
                await onConfirmSubmit();
                // Code already marked as attempted at start of function
                // If it succeeded, we'll be redirected anyway
                // If it failed, the mark prevents infinite retry loop
                return;
            }

            // First verify the email
            const verifyResponse = await confirmEmail(
                formData.email,
                formData.verificationCode,
                csrfToken
            );

            if (verifyResponse.data.error) {
                onLoginError("Invalid verification code. Please try again.");
                // Code already marked as attempted at start of function
                return;
            }

            // Email verified, now attempt login
            await handleLogin(
                formData.email,
                formData.password,
                {
                    onMfaSetup: (mfaData) => onStepTransition('setupMFA', null, mfaData),
                    onMfaVerify: (mfaData) => onStepTransition('verifyMFA', null, mfaData),
                    onError: (error) => onLoginError(error),
                    onSuccess: (claims) => {
                        // Clear the auto-submit tracking on success
                        lastAutoSubmittedCode.current = null;
                        onLoginSuccess({ ...claims, email: formData.email });
                    }
                }
            );
        } catch (err) {
            console.error('Email verification error:', err);
            onLoginError("Email verification failed. Please try again.");
            // Code already marked as attempted at start of function
        } finally {
            setIsLoading(false);
        }
    }, [formData?.email, formData?.password, formData?.verificationCode, isLoading, csrfToken, handleLogin, onStepTransition, onLoginError, onLoginSuccess, setIsLoading, isConfirmFlow, onConfirmSubmit]);

    // Keep handleSubmit ref up to date
    useEffect(() => {
        handleSubmitRef.current = handleSubmit;
    }, [handleSubmit]);

    // Auto-submit when code is complete
    // Only auto-submit if we haven't already attempted this code (prevents infinite retries on error)
    useEffect(() => {
        const currentCode = formData?.verificationCode;
        const hasNotBeenAttempted = lastAutoSubmittedCode.current !== currentCode;
        const canAutoSubmit = currentCode?.length === 6 && 
                              !isLoading && 
                              !disabled && 
                              isVisible && 
                              hasNotBeenAttempted &&
                              !isAutoSubmitting.current;
        
        if (canAutoSubmit && handleSubmitRef.current) {
            isAutoSubmitting.current = true;
            // Don't set lastAutoSubmittedCode here - let handleSubmit do it after attempting
            handleSubmitRef.current({ preventDefault: () => {} }).finally(() => {
                isAutoSubmitting.current = false;
            });
        }
    }, [formData?.verificationCode, isLoading, disabled, isVisible]);
    
    // Reset the auto-submit tracking when the code changes (allows retry after user edits)
    useEffect(() => {
        const currentCode = formData?.verificationCode;
        // If the code is different from what we last attempted, reset tracking to allow auto-submit
        if (lastAutoSubmittedCode.current && lastAutoSubmittedCode.current !== currentCode) {
            lastAutoSubmittedCode.current = null;
            isAutoSubmitting.current = false;
        }
    }, [formData?.verificationCode]);

    // Safety check for formData - must be after all hooks
    if (!formData) {
        return <div>Loading...</div>;
    }

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    if (disabled) {
        return <DisabledFormState message="Email Verified" tooltip="Added" />
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                borderRadius: '8px',
                margin: '8px 0',
                transition: 'all 0.3s ease'
            }}
        >
            {/* Show email input for confirm flow when email is not provided */}
            <Box sx={{ mt: -1 }}>
                <WithActions
                    forceSpacing
                    inputType="text"
                >
                    <Typography
                        component="div"
                        sx={{
                            color: theme.colors.text,
                            textAlign: 'left',
                            width: '100%',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            margin: 0,
                            padding: 0,
                            lineHeight: '40px',
                            height: '40px',
                            display: 'block',
                        }}
                    >
                        Verify Email
                    </Typography>
                </WithActions>
            </Box>
            <br/>
            {isConfirmFlow && (!formData?.email || formData.email.length === 0) && (
            <WithActions
                showSuccessButton={disabled}
                isVisible={true}
                successTooltip="verified"
                inputType="text"
                forceSpacing={true}
                fullWidth
            >
                <InputEmail
                    onEmailChange={onChangeAuthForm}
                    showValidation={false}
                />
            </WithActions>
            )}
            <Box
                sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
                data-keeper-disable="true"
                data-keeper-ignore="true"
                data-password-manager="disabled"
            >
                <div data-keeper-ignore="true" data-password-manager="disabled">
                    <WithActions
                        disableKeeper={true}
                        showRefreshButton={true}

                        // showSuccessButton={disabled}
                        isVisible={true}
                        inputType="code"
                        forceSpacing={true}
                        fullWidth
                        onResend={() => {
                            // TODO: Add resend functionality here
                        }}
                        isResending={isLoading}
                        refreshTooltip="Resend verification email"
                    >
                        <OTPInputField
                            verificationCode={formData.verificationCode}
                            onChangeAuthForm={onChangeAuthForm}
                            name="verificationCode"
                            disableKeeper={true}
                            disabled={disabled}
                        />
                    </WithActions>
                </div>
            </Box>
            <WithActions forceSpacing={true} isButton={true}>
                <ButtonPrimary type="submit" disabled={disabled || isLoading}>
                    {isLoading ? 'checking...' : (isConfirmFlow ? 'Confirm' : 'Submit')}
                </ButtonPrimary>
            </WithActions>
        </Box>
    );
};

export default VerifyEmailForm;
